import { supabase } from './supabase';

// Module-level token kept in sync by AuthContext via updateAccessToken().
// This is the primary source for auth headers so API calls always use the
// same token the app already knows is valid, rather than re-reading from
// Supabase's storage (which can be null on the first render cycle).
let _accessToken: string | null = null;

export function updateAccessToken(token: string | null) {
  _accessToken = token;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  // Always ask Supabase for the session — it auto-refreshes expired tokens.
  const { data: { session } } = await supabase.auth.getSession();
  // Fall back to the module-level token for the rare first-render cycle where
  // getSession() returns null before Supabase has loaded from storage.
  const token = session?.access_token ?? _accessToken;
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

async function handleResponse<T>(res: Response): Promise<T> {
  const data = await res.json();
  if (res.status === 401) {
    // Session expired or revoked server-side — sign out and redirect to login immediately
    await supabase.auth.signOut({ scope: 'local' });
    const siteUrl = import.meta.env.VITE_DISCGO_SITE_URL || 'http://localhost:3000';
    window.location.href = `${siteUrl}/login`;
    throw new Error('SESSION_EXPIRED');
  }
  if (!res.ok) throw new Error(data.error || `API error ${res.status}`);
  return data as T;
}

export async function apiPost<T = unknown>(path: string, body: unknown): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

export async function apiGet<T = unknown>(path: string): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(path, { headers });
  return handleResponse<T>(res);
}

export async function apiDelete<T = unknown>(path: string): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(path, { method: 'DELETE', headers });
  return handleResponse<T>(res);
}
